import type { ComponentTypesV2 } from 'types/selectComponent'
import { mapComponentTypesV2ToV1 } from 'util/misc'
import { AC_VOLTAGE_RATING_BUCKET_FILTER_KEY_V2 } from '../acVoltageRating/constants'
import BasicFilter from '../basic/BasicFilter'
import { CURRENT_RATING_FILTER_GTE_KEY, CURRENT_RATING_FILTER_LTE_KEY } from '../currentRating/constants'
import { PHASE_FILTER_KEY_V2 } from '../phaseType/PhaseTypeFilterNodeFactoryV2'
import type {
  ConfigurableFilterOptionsType,
  FilterComponentNodeV2,
  FilterGenericPropsType,
  FilterValuesType,
  OptionGenericType,
} from '../typeV2'
import { parseStringifiedListFilterValue } from '../utils/utils'
import { VOLTAGE_AND_CURRENT_FILTER_KEY_V2 } from '../voltageAndCurrent/VoltageAndCurrentFilterNodeFactoryV2'

export const CURRENT_FILTER_KEY_V2 = 'current_type'
const defaultValue = ''

export const isCurrentTypeFilterAvailableV2 = (filterValues: FilterValuesType) => {
  const currentTypes = parseStringifiedListFilterValue<ComponentTypesV2>({
    filterValues,
    key: 'types',
  })
  return currentTypes.has('cable') || currentTypes.has('isolator')
}

const CURRENT_TYPE_OPTIONS: OptionGenericType<string>[] = [
  { id: 'default', title: 'All', value: defaultValue },
  { id: 'ac', title: 'AC', value: 'ac' },
  { id: 'dc', title: 'DC', value: 'dc' },
]

export const createCurrentTypeFilterNode = <T,>({
  label = 'Current Type',
  ...restConfigs
}: ConfigurableFilterOptionsType<string, T>): FilterComponentNodeV2 => {
  const FilterComponent = (filterProps: FilterGenericPropsType) => {
    const types = parseStringifiedListFilterValue<ComponentTypesV2>({
      filterValues: filterProps.allFilterValues,
      key: 'types',
    })

    if (mapComponentTypesV2ToV1(Array.from(types)[0]) !== 'other') {
      return null
    }

    const disabled = !isCurrentTypeFilterAvailableV2(filterProps.allFilterValues)
    const currentTypeFilterSideEffect = ({
      filterValues,
      value,
    }: {
      filterValues: FilterValuesType
      modifiableFilterValues: FilterValuesType
      value: unknown
      selectedOption: unknown
    }): FilterValuesType => {
      const nextFilterValues = { ...filterValues }
      const selectedCurrentType = String(value ?? '')

      const clearACOnlyFilters = () => {
        delete nextFilterValues[PHASE_FILTER_KEY_V2]
        delete nextFilterValues[CURRENT_RATING_FILTER_GTE_KEY]
        delete nextFilterValues[CURRENT_RATING_FILTER_LTE_KEY]
        delete nextFilterValues[AC_VOLTAGE_RATING_BUCKET_FILTER_KEY_V2]
      }

      const clearDCOnlyFilters = () => {
        delete nextFilterValues[VOLTAGE_AND_CURRENT_FILTER_KEY_V2]
      }

      if (selectedCurrentType === 'ac') {
        clearDCOnlyFilters()
      } else if (selectedCurrentType === 'dc') {
        clearACOnlyFilters()
      } else {
        // Keep hidden AC/DC-specific values in state so returning to a specific
        // current type can restore the previous constraints.
      }

      return nextFilterValues
    }

    return (
      <BasicFilter
        label={label}
        defaultSelectedOptionValue={defaultValue}
        options={CURRENT_TYPE_OPTIONS}
        filterKey={CURRENT_FILTER_KEY_V2}
        sideEffect={currentTypeFilterSideEffect}
        {...restConfigs}
        {...filterProps}
        disabled={disabled}
      />
    )
  }

  return {
    key: CURRENT_FILTER_KEY_V2,
    component: FilterComponent,
  }
}

export default createCurrentTypeFilterNode
